import React, { useState } from 'react';
import { Backup } from '../../types/api';
import WalInfoBadge from './WalInfoBadge';
import { Modal, Button } from '../ui';
import { useTranslation } from 'react-i18next';

interface RestoreConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  backup: Backup;
  /** Keycloak dont la base vit sur ce cluster. CNPG restaure un CLUSTER, pas une
   *  base : les restaurer sans le dire ferait perdre en silence tous les
   *  utilisateurs et clients crees depuis la sauvegarde. */
  hostedApps?: Array<{ deployment_id: string; nom: string; type_application: string; label: string }>;
  loading?: boolean;
}

const RestoreConfirmModal: React.FC<RestoreConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  backup,
  hostedApps = [],
  loading = false
}) => {
  const { t } = (useTranslation as any)('backups');
  const [confirmed, setConfirmed] = useState(false);

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  const handleConfirm = () => {
    onConfirm();
  };

  const handleClose = () => {
    setConfirmed(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('restoreConfirm.title') as string} size="lg">
      <div className="p-6">
        {/* Header icon */}
        <div className="flex justify-center mb-4">
          <div className="flex items-center justify-center h-12 w-12 rounded-full bg-blue-500/12">
            <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        </div>

        <p className="text-sm text-text-tertiary text-center mb-6">
          {t('restoreConfirm.blueGreenDesc')}
        </p>

        <div className="bg-blue-500/12 border border-blue-500/20 rounded-lg p-4 mb-6">
          <h4 className="text-sm font-semibold text-blue-400 mb-2">
            {t('restoreConfirm.importantInfo')}
          </h4>
          <ul className="text-xs text-blue-400 space-y-1 list-disc list-inside">
            <li>Un nouveau cluster PostgreSQL sera créé avec le snapshot du backup</li>
            <li>Les données restaurées correspondront exactement à l'état au moment du backup</li>
            <li>Vous devrez valider manuellement avant que le trafic ne bascule</li>
            <li>L'ancien cluster restera actif jusqu'à validation</li>
            <li>Vous pourrez annuler la restauration avant validation</li>
          </ul>
        </div>

        {hostedApps.length > 0 && (
          <div className="bg-red-500/12 border border-red-500/30 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-semibold text-red-400 mb-2">
              {t('restoreConfirm.keycloakWarningTitle')}
            </h4>
            <p className="text-xs text-red-400/90 mb-2">
              {t('restoreConfirm.keycloakWarningBody', {
                apps: hostedApps.map((k) => `${k.label} « ${k.nom} »`).join(', '),
              })}
            </p>
            <p className="text-xs text-red-400/90">{t('restoreConfirm.keycloakWarningScope')}</p>
          </div>
        )}

        <div className="bg-surface-0 rounded-lg border border-border-1 p-4 mb-6 space-y-2 text-sm">
          <div>
            <span className="text-text-tertiary">Backup:</span>
            <p className="text-text-primary font-medium">{backup.backup_name}</p>
          </div>
          <div>
            <span className="text-text-tertiary">Créé le:</span>
            <p className="text-text-primary">{formatDate(backup.started_at)}</p>
          </div>
          {backup.size_bytes && (
            <div>
              <span className="text-text-tertiary">Taille:</span>
              <p className="text-text-primary">{(backup.size_bytes / (1024 * 1024 * 1024)).toFixed(2)} GB</p>
            </div>
          )}
        </div>

        {/* Info WAL et type de restore */}
        <div className="mb-6">
          <WalInfoBadge backup={backup} mode="detailed" />
        </div>

        <div className="mb-6">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1 h-4 w-4 text-accent-600 focus:ring-accent-500 border-border-1 rounded"
            />
            <span className="ml-3 text-sm text-text-primary">
              Je comprends que cette opération va créer un nouveau cluster avec le snapshot du backup et que <strong>toutes les données après {formatDate(backup.started_at)}</strong> seront perdues. Je devrai valider manuellement avant que le trafic ne bascule.
            </span>
          </label>
        </div>
      </div>

      {/* Footer - sticky at bottom */}
      <div className="flex justify-end gap-3 p-4 border-t border-border-1 sticky bottom-0 bg-surface-2">
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={loading}
        >
          {t('common:cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!confirmed}
          loading={loading}
        >
          {t('restoreConfirm.confirmButton')}
        </Button>
      </div>
    </Modal>
  );
};

export default RestoreConfirmModal;
